import React, { useEffect } from "react";
import {
    Notification,
    STATUS_TYPES,
    yup,
    generateResolver,
    VALIDATORS,
    DateInput,
    TimeInput,
    CloseableContent
} from "dyl-components";
import {
    Button,
    Form,
    Segment,
    Grid,
    TextArea,
    Header,
    Icon,
    Popup
} from "semantic-ui-react";

import { useDispatch, useSelector } from "react-redux";
import { DateTimeUtils } from "dyl-components";

import selfReportActions from "actions/self_report";
import "./index.scss";
import { Controller, useForm } from "react-hook-form";
import { Link } from 'react-router-dom';
import { PhoneUtil } from "utils";
import relationshipsActions from 'actions/relationships';

const DIRECTION_OPTIONS = [
    {
        key: "outbound",
        value: "outbound",
        text: "Outbound",
    },
    {
        key: "inbound",
        value: "inbound",
        text: "Inbound",
    },
];

const NewActivityForm = ({onClose, isCreatingEvent, emailOnly, contact, account, fromEmail, isContact, hideActivitySelection, textOnly, refresh }) => {
    const isOnlyForEmail = emailOnly || fromEmail;
    const {
        control,
        watch,
        reset,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            activityType: (() => {
                if (isOnlyForEmail) {
                    return "email"
                }
                if (textOnly) {
                    return "text"
                }
                return "call"
            })(),
            date: "",
            time: "",
            numberOfHours: "",
            numberOfMinutes: "",
            direction: "outbound",
            result: "Reached",
            subject: "",
            body: "",
            note: "",
            message:"",
            relatedTo: ""
        },
        resolver: generateResolver({
            date: yup.string().required("This field is required"),
            time: yup.string().required("This field is required"),
            numberOfHours: yup.string().only_numbers(),
            numberOfMinutes: yup.string().only_numbers(),
            note: VALIDATORS.NOTE(),
            message: yup.string().when("activityType", {
                is: "text",
                then: (schema) => schema.required("This field is required"),
                otherwise: (schema) => schema.notRequired()
            }),
            body: yup.string().when("activityType", {
                is: "email",
                then: (schema) => schema.required("This field is required"),
                otherwise: (schema) => schema.notRequired()
            })
        }),
    });

    const activityTypeWatch = watch("activityType");
    const directionWatch = watch("direction");
    const [relationOptions, setRelationOptions] = React.useState([]);

    const dispatch = useDispatch();

    const {
        isReading,
        contact_id,
        from,
        assigned_user,
        mainEmail,
        mainPhone,
        account_id,
        hasEmail,
        hasPhone
    } = useSelector((state) => {
        const { first_name = "", last_name = "" } = state.users.userProfile;
        let mainPhone = { phone: "No main phone" };
        const hasPhone = state.contact_phone?.phone?.some?.(
            (p) => p?.phone && p?.phone?.trim?.().length > 0,
        );
        const hasEmail = state.contact_email?.email?.some?.(
            (e) => e?.email && e?.email?.trim?.()?.length > 0,
        );

        if (state.contact_phone.phone) {
            mainPhone = state.contact_phone.phone.find((item) => item.main);

            if (!mainPhone) {
                mainPhone = state.contact_phone.phone[0] || { phone: "No main phone" };
            }
        }

        let mainEmail = state.contact_email.email.find((item) => item.main);

        if (!mainEmail) {
            mainEmail = state.contact_email.email[0] || { email: "No main email" };
        }
        return {
            isCreatingActivity:
                state.self_report.isLoggingCall ||
                state.self_report.isLoggingText ||
                state.self_report.isLoggingEmail,
            isReading: state.users.isReadingUsers,
            contact_id: Number(state.contact.contact_id),
            from: `${first_name} ${last_name}`,
            assigned_user: state.auth.user_id,
            mainEmail: mainEmail.email,
            mainPhone: mainPhone.phone,
            account_id: state?.account?.account?.id || state.contact.account_id,
            hasEmail,
            hasPhone
        };
    });

    const TYPE_OPTIONS = [
        {
            key: "call",
            value: "call",
            text: "Call",
            disabled: !hasPhone,
        },
        {
            key: "text",
            value: "text",
            text: "Text",
            disabled: !hasPhone,
        },
        {
            key: "email",
            value: "email",
            text: "Email",
            disabled: !hasEmail,
        },
    ];
    const recipientContact = contact?.id || contact_id;
    const recipientAccount = account?.id || account_id;

    const addActivity = (payload, type) => {
        const { user_id, occurred, direction, notes, contact_id, from, to, account_id, related_to_external_id } = payload;
        const commonActivityProps = {
            user_id,
            occurred,
            direction,
            notes,
            contact_id: contact_id ? Number(contact_id) : undefined,
            account_id: account_id ? Number(account_id) : undefined,
            source: from,
            destination: to,
            related_type: relationOptions.find(option => option.key === related_to_external_id)?.value,
            related_to_external_id
        };

        switch (type) {
            case "call":
                const { result, length } = payload;
                return dispatch(
                    selfReportActions.addCallActivity({
                        ...commonActivityProps,
                        result,
                        length,
                    })
                );
            case "text":
                const { message } = payload;
                return dispatch(
                    selfReportActions.addTextActivity({
                        ...commonActivityProps,
                        messages: message,
                    })
                );
            case "email":
                const { subject, body } = payload;
                return dispatch(
                    selfReportActions.addEmailActivity({
                        ...commonActivityProps,
                        subject,
                        body,
                    })
                );
            default:
                Notification.alert(
                    `Failed to add activity of type ${type}`,
                    STATUS_TYPES.ERROR,
                    true
                );
                return Promise.reject();
        }
    }

    const readActivityLog = (contact_id, account_id, isContact) => {
        dispatch(
            selfReportActions.readActivity({
                ...(isContact ? {contact_id} : {}),
                ...(!isContact ? {account_id} : {}),
                occurred: DateTimeUtils.getCurrentDate(null, true, false),
            })
        );
    }

    const getMainEmail = (contact) => {
        if (contact.email && contact.email.email) {
            return contact.email.email;
        }
        else if (contact.email){
            return contact.email;
        }
        return "No main email"
    }

    const onAdd = async ({
        activityType,
        date,
        time,
        numberOfHours,
        numberOfMinutes,
        direction,
        result,
        subject,
        body,
        note,
        message,
        relatedTo
    }) => {
        try {
            const email = emailOnly ? getMainEmail(isContact ? contact : account) : mainEmail;
            const occurred = DateTimeUtils.getUnixTime(
                `${date} ${time}`,
                DateTimeUtils.DATETIME_FORMAT
            );
            const payload = {
                user_id: assigned_user,
                occurred,
                direction,
                notes: note,
                ...(isContact
                    ? { contact_id: recipientContact, account_id: recipientAccount }
                    : { account_id: recipientAccount }),
                from,
                to: activityType === "email" ? email : mainPhone,
                result: direction === DIRECTION_OPTIONS[1].value ? "" : result,
                length: DateTimeUtils.convertTimeToSeconds(
                    `${numberOfHours || 0}:${numberOfMinutes || 0}`,
                    "HH:mm"
                ),
                related_to_external_id: relationOptions?.find?.((option) => option.value === relatedTo)?.key,
                message,
                body,
                subject
            };
            await addActivity(payload, activityType);
            Notification.alert(
                "Successfully added activity!",
                STATUS_TYPES.SUCCESS,
                true
            );
            onClear();
            readActivityLog(
                emailOnly ? recipientContact : undefined,
                account_id,
                isContact
            );
            if (refresh) {
                refresh();
            }
            onClose(true);
        } catch (error) {
            console.log(error)
            Notification.alert(
                "Failed to add activity",
                STATUS_TYPES.ERROR,
                true
            );
        }
    };


    const formatRelatedToOptions = (relations) => {
        let options = relations.map(
            (r) => {
                const [first,] = r.related_name.split("-");
                const isLead = first === "Lead "
                const className_name = {
                    customer: 'fas fa-user-crown',
                    customer_pipeline: 'fas fa-crown',
                    pipeline_stage: isLead ? 'fas fa-address-card' : 'fas fa-filter-circle-dollar'
                }[r.related_type] || '';
                const value = r?.related_type;
                const text = <><Icon className={className_name} />{r.related_name || r.contact_name}</>
                return { key: r.related_id, value: value !== "pipeline_stage" ? value : "pipeline", text }
            }
        )
        setRelationOptions(options || []);
    }
    
    useEffect(() => {
        const person_id = isContact ? recipientContact : recipientAccount;

        if (person_id) {
            dispatch(relationshipsActions.getContactRelations({ person_id : [person_id] }))
                .then((relations) => formatRelatedToOptions(relations))
                .catch((err) => console.log(err));
        }
    }, [dispatch, account_id, isContact, recipientContact, recipientAccount]);

    const onClear = () => {
        reset();
    };

    const getCurrentDate = () => {
        return DateTimeUtils.getCurrentDate();
    }

    const handleClose = () => {
        onClear();
        onClose();
    };
    return (
        <Segment
            className="ActivityFormContainer"
            loading={isCreatingEvent || isReading}
        >
        <CloseableContent onClose={handleClose}>
            <Form loading={isSubmitting}>
                <Grid stackable className="ActivityFormBody">
                    <Grid.Row className="ActivityFormBody__RowHeader">
                        <Grid.Column>
                            <Header className="ActivityForm__Header">
                                Log Activity
                            </Header>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="ActivityFormBody__Row">
                        <Grid.Column width={8} className="ActivityFormBody__LeftCol"> 
                            <Controller
                                name="activityType"
                                control={control}
                                render={({ field: { value, name, onChange } }) => (
                                    <Form.Select
                                        className="ActivityForm__typeField"
                                        label='Activity Type'
                                        name={name}
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                        placeholder='Type'
                                        selectOnBlur={false}
                                        options={TYPE_OPTIONS}
                                        disabled={emailOnly || textOnly}
                                        required
                                        fluid
                                        {...(hideActivitySelection ? ({
                                            open: false,
                                            icon: null
                                        }) : {})}
                                    />
                                )}
                            />
                        </Grid.Column>
                        <Grid.Column width={8} className="ActivityFormBody__RightCol">
                            <Controller
                                name="relatedTo"
                                control={control}
                                render={({ field: { value, name, onChange } }) => (
                                    <Form.Select
                                        className="ActivityForm__typeField ActivityForm__relatedToField"
                                        label='Related To'
                                        name={name}
                                        value={value}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                        placeholder='Related To'
                                        selectOnBlur={false}
                                        options={relationOptions}
                                        disabled={relationOptions.length === 0}
                                        fluid
                                    />
                                )}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="ActivityFormBody__Row">
                        <Grid.Column width={8} className="ActivityFormBody__LeftCol">
                            <Controller
                                name="date"
                                control={control}
                                render={({
                                    field: {
                                        value,
                                        onChange
                                    },
                                }) => (
                                    <Form.Field
                                        control={DateInput}
                                        minDate="01-01-1970"
                                        maxDate={getCurrentDate()}
                                        value={value}
                                        onChange={(_, { value }) => {
                                            onChange(value);
                                        }}
                                        placeholder="Select Date"
                                        label="Date"
                                        required
                                    />
                                )}
                            />
                        </Grid.Column>
                        <Grid.Column width={8} className="ActivityFormBody__RightCol">
                            <Controller
                                name="time"
                                control={control}
                                render={({
                                    field: {
                                        value,
                                        onChange
                                    },
                                }) => (
                                    <Form.Field
                                        control={TimeInput}
                                        value={value}
                                        onChange={(_, { value }) => {
                                            onChange(value);
                                        }}
                                        label="Time"
                                        required
                                        placeholder="Select Time"
                                    />
                                )}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    {activityTypeWatch === "call" && (
                        <Grid.Row columns="equal" className="ActivityFormBody__Row">
                            <Grid.Column>
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column
                                            width={8}
                                            className="ActivityFormBody__LeftCol"
                                        >
                                            <Controller
                                                name="numberOfHours"
                                                control={control}
                                                render={({ field: { value, name, onChange }, fieldState: { error } }) => (
                                                    <Form.Input
                                                        label="Duration"
                                                        value={value}
                                                        name={name}
                                                        onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                                        placeholder="Hour"
                                                        style={{ width: "100%" }}
                                                        error={error?.message}
                                                    />
                                                )}
                                            />
                                        </Grid.Column>
                                        <Grid.Column
                                            width={8}
                                            className="ActivityFormBody__RightCol--minDuration"
                                        >
                                            <Controller
                                                name="numberOfMinutes"
                                                control={control}
                                                render={({ field: { value, name, onChange }, fieldState: { error } }) => (
                                                    <Form.Input
                                                        value={value}
                                                        name={name}
                                                        onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                                        placeholder="Min"
                                                        style={{ width: "100%" }}
                                                        error={error?.message}
                                                    />
                                                )}
                                            />
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {activityTypeWatch !== "call" && (
                        <Grid.Row columns="equal" className="ActivityFormBody__Row">
                            <Grid.Column>
                                <Controller
                                    name="direction"
                                    control={control}
                                    render={({ field: { value, name, onChange } }) => (
                                        <Form.Select
                                            label='Direction'
                                            name={name}
                                            value={value}
                                            onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                            placeholder='Activity'
                                            options={DIRECTION_OPTIONS}
                                            required
                                            fluid
                                        />
                                    )}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {activityTypeWatch === "call" && (
                        <Grid.Row className="ActivityFormBody__Row">
                            <Grid.Column
                                width={8}
                                className="ActivityFormBody__LeftCol"
                            >
                                <Controller
                                    name="direction"
                                    control={control}
                                    render={({ field: { value, name, onChange } }) => (
                                        <Form.Select
                                            label='Direction'
                                            name={name}
                                            value={value}
                                            onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                            placeholder='Activity'
                                            options={DIRECTION_OPTIONS}
                                            required
                                            fluid
                                        />
                                    )}
                                />
                            </Grid.Column>
                            <Grid.Column
                                width={8}
                                className="ActivityFormBody__RightCol"
                            >
                                <Controller
                                    name="result"
                                    control={control}
                                    render={({ field: { value, name, onChange } }) => (
                                        <Form.Select
                                            label='Call Result'
                                            name={name}
                                            value={value}
                                            onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                            placeholder='Call Result'
                                            options={PhoneUtil.CALL_RESULT_OPTIONS}
                                            disabled={directionWatch === "inbound"}
                                            fluid
                                        />
                                    )}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {activityTypeWatch === "email" && (
                        <Grid.Row className="ActivityFormBody__Row">
                            <Grid.Column>
                                <Controller
                                    name="subject"
                                    control={control}
                                    render={({ field: { value, name, onChange } }) => (
                                        <Form.Input
                                            label="Subject"
                                            value={value}
                                            name={name}
                                            onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                            placeholder="Type Subject"
                                            style={{ width: "100%" }}
                                        />
                                    )}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {activityTypeWatch === "email" && (
                        <Grid.Row
                            columns="equal"
                            className="ActivityFormBody__TextAreaRow"
                        >
                            <Grid.Column>
                                <Controller
                                    name="body"
                                    control={control}
                                    render={({ field: { value, name, onChange } }) => (
                                        <Form.Field
                                            className="ActivityFormBody__textArea"
                                            control={TextArea}
                                            label="Body"
                                            value={value}
                                            name={name}
                                            onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                            placeholder="Type Body"
                                            rows={4}
                                            size="tiny"
                                            required
                                        />
                                    )}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {watch("activityType") === "text" &&<Grid.Row
                        columns="equal"
                        className="ActivityFormBody__TextAreaRow"
                    >
                        <Grid.Column>
                            <Controller
                                name="message"
                                control={control}
                                render={({ field: { value, name, onChange }, fieldState: { error } }) => (
                                    <Form.Field
                                        className="ActivityFormBody__textArea"
                                        control={TextArea}
                                        label="Message"
                                        value={value}
                                        name={name}
                                        required
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                        placeholder="Type Message"
                                        rows={4}
                                        size="tiny"
                                        error={error?.message}
                                    />
                                )}
                            />
                        </Grid.Column>
                    </Grid.Row>}
                    <Grid.Row
                        columns="equal"
                        className="ActivityFormBody__TextAreaRow"
                    >
                        <Grid.Column>
                            <Controller
                                name="note"
                                control={control}
                                render={({ field: { value, name, onChange }, fieldState: { error } }) => (
                                    <Form.Field
                                        className="ActivityFormBody__textArea"
                                        control={TextArea}
                                        label="Note"
                                        value={value}
                                        name={name}
                                        onChange={(_, { value }) => { onChange({ target: { name, value } }); }}
                                        placeholder="Type Note"
                                        rows={4}
                                        size="tiny"
                                        error={error?.message}
                                    />
                                )}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    {emailOnly && (
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Header as='h5'>
                                    Go to <Link to={`/settings/users/${assigned_user}/integration`}><Icon name='cog' style={{ color: 'primary', fontSize: 15, margin: 0, verticalAlign: 'center' }} />Settings</Link> to add an email integration
                                </Header>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    <Grid.Row>
                        <Grid.Column
                            className="ActivityForm__Row"
                            floated="right"
                            width={10}
                        >
                            <div className="ActivityFormBody__Buttons">
                                <Button
                                    className="ActivityFormBody__CancelButton"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    disabled={!isValid || !isDirty || isSubmitting}
                                    onClick={handleSubmit(onAdd)}
                                    className="ActivityFormBody__SaveButton"
                                >
                                    Save
                                </Button>
                            </div>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Form>
        </CloseableContent>
        </Segment>
    );
};

export const ActivityPopup = ({ trigger, activityProps, open, onOpen, onClose, ...popupProps }) => {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const isOpen = open !== undefined ? open : internalOpen;

    const handleOpen = () => {
        if (onOpen) onOpen();
        setInternalOpen(true);
    };

    const handleClose = (e) => {
        if (e && e.target) {
            if (
                (e.target.closest && e.target.closest(".ui.popup")) ||
                (typeof e.target.className === "string" && e.target.className.includes("suicr"))
            ) {
                return;
            }
        }
        if (onClose) onClose();
        setInternalOpen(false);
    };

    const handleFormClose = () => {
        if (onClose) onClose();
        setInternalOpen(false);
    };

    return (
        <Popup
            className="AddForm"
            trigger={trigger}
            content={<NewActivityForm {...activityProps} onClose={handleFormClose} />}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            closeOnDocumentClick={true}
            on="click"
            position="bottom left"
            pinned
            basic
            wide
            {...popupProps}
        />
    );
};

export default NewActivityForm;
